import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
    Save, Wallet, Bitcoin, ShieldAlert, ArrowDownToLine, RefreshCw,
    PieChart, Users, Coins, AlertTriangle, Banknote, Network, Scale,
} from "lucide-react";

// Theme tokens per group — match the AmbassadorsTab visual treatment
const GROUP_THEMES = {
    destinos: {
        border: "border-p2w-green/30",
        glow: "bg-p2w-green",
        badgeBorder: "border-p2w-green/40",
        badgeBg: "bg-p2w-green/15",
        icon: "text-p2w-green",
        pill: "text-p2w-green",
        fieldHover: "hover:border-p2w-green/40",
        focus: "focus-visible:ring-p2w-green/40",
    },
    retiros: {
        border: "border-p2w-blue/30",
        glow: "bg-p2w-blue",
        badgeBorder: "border-p2w-blue/40",
        badgeBg: "bg-p2w-blue/15",
        icon: "text-p2w-blue",
        pill: "text-p2w-blue",
        fieldHover: "hover:border-p2w-blue/40",
        focus: "focus-visible:ring-p2w-blue/40",
    },
    pozo: {
        border: "border-p2w-purple/30",
        glow: "bg-p2w-purple",
        badgeBorder: "border-p2w-purple/40",
        badgeBg: "bg-p2w-purple/15",
        icon: "text-p2w-purple",
        pill: "text-p2w-purple",
        fieldHover: "hover:border-p2w-purple/40",
        focus: "focus-visible:ring-p2w-purple/40",
    },
    referidos: {
        border: "border-p2w-magenta/30",
        glow: "bg-p2w-magenta",
        badgeBorder: "border-p2w-magenta/40",
        badgeBg: "bg-p2w-magenta/15",
        icon: "text-p2w-magenta",
        pill: "text-p2w-magenta",
        fieldHover: "hover:border-p2w-magenta/40",
        focus: "focus-visible:ring-p2w-magenta/40",
    },
    equivalencia: {
        border: "border-p2w-amber/30",
        glow: "bg-p2w-amber",
        badgeBorder: "border-p2w-amber/40",
        badgeBg: "bg-p2w-amber/15",
        icon: "text-p2w-amber",
        pill: "text-p2w-amber",
        fieldHover: "hover:border-p2w-amber/40",
        focus: "focus-visible:ring-p2w-amber/40",
    },
};

// Fields per source (wallet_config vs platform settings)
const WALLET_FIELDS = [
    { k: "brebkey", label: "Número de llave Bre-B", icon: Wallet, type: "text", help: "Visible en /wallet/recargar para que los usuarios transfieran.", group: "destinos", scope: "wallet" },
    { k: "usdt_trc20_address", label: "Dirección USDT (red TRC20)", icon: Bitcoin, type: "text", help: "Dirección donde recibirás recargas en USDT.", group: "destinos", scope: "wallet" },
    { k: "usdt_to_cop", label: "USDT → COP (referencial)", icon: RefreshCw, type: "number", step: 1, help: "Tasa de conversión usada para acreditar puntos.", group: "destinos", scope: "wallet" },
    { k: "min_withdraw", label: "Retiro mínimo (pts/COP)", icon: ArrowDownToLine, type: "number", step: 1000, help: "Monto mínimo para solicitar un retiro.", group: "retiros", scope: "wallet" },
    { k: "withdraw_daily_max", label: "Máximo diario sin retención", icon: ArrowDownToLine, type: "number", step: 10000, help: "Si la suma de retiros 24h supera esto, se aplica retención.", group: "retiros", scope: "wallet" },
    { k: "withholding_pct", label: "% de retención", icon: ShieldAlert, type: "percent", step: 0.01, help: "Ej. 0.20 = 20%.", group: "retiros", scope: "wallet" },
    { k: "withholding_applies", label: "La retención se aplica sobre", icon: ShieldAlert, type: "select", options: [["excess", "Excedente del máximo"], ["total", "Monto total"]], help: "Define la base sobre la que se calcula.", group: "retiros", scope: "wallet" },
    { k: "withholding_label", label: "Etiqueta legal de la retención", icon: ShieldAlert, type: "text", help: "Texto que verá el usuario en el breakdown.", group: "retiros", scope: "wallet" },
];

const SETTINGS_FIELDS = [
    { k: "platform_pct", label: "% Plataforma", icon: PieChart, type: "percent", step: 0.01, help: "Comisión de Play2Win sobre el pozo. Ej. 0.15 = 15%.", group: "pozo", scope: "settings" },
    { k: "creator_pct", label: "% Creador", icon: PieChart, type: "percent", step: 0.01, help: "Comisión del creador del mercado.", group: "pozo", scope: "settings" },
    { k: "league_pct", label: "% Liga", icon: PieChart, type: "percent", step: 0.01, help: "Comisión destinada a la liga / fondo común.", group: "pozo", scope: "settings" },
    { k: "l1_bonus", label: "Bono referido nivel 1", icon: Users, type: "number", step: 50, help: "Puntos al referidor directo.", group: "referidos", scope: "settings" },
    { k: "l2_bonus", label: "Bono referido nivel 2", icon: Users, type: "number", step: 50, help: "Puntos al referidor de nivel 2.", group: "referidos", scope: "settings" },
    { k: "l3_bonus", label: "Bono referido nivel 3", icon: Users, type: "number", step: 50, help: "Puntos al referidor de nivel 3.", group: "referidos", scope: "settings" },
    { k: "point_to_peso_rate", label: "Puntos por peso", icon: Coins, type: "number", step: 0.01, help: "Equivalencia mostrada al usuario (1.0 = 1 punto vale 1 peso).", group: "equivalencia", scope: "settings" },
];

const ALL_FIELDS = [...WALLET_FIELDS, ...SETTINGS_FIELDS];

const GROUPS = [
    { id: "destinos", title: "Destinos de recarga", desc: "Datos donde los usuarios envían su pago. Los cambios son visibles inmediatamente en /wallet/recargar.", headerIcon: Banknote },
    { id: "retiros", title: "Reglas de retiro y retención", desc: "Define los límites, mínimos y la retención aplicada cuando un usuario supera el máximo diario.", headerIcon: ArrowDownToLine },
    { id: "pozo", title: "Reparto del pozo (comisiones)", desc: "Cómo se reparte cada pozo al resolver un mercado. La suma debe dejar margen para los jugadores.", headerIcon: PieChart },
    { id: "referidos", title: "Programa de referidos", desc: "Puntos otorgados por cada nivel del árbol de referidos.", headerIcon: Network },
    { id: "equivalencia", title: "Equivalencia puntos / peso", desc: "Tasa que se usa para mostrar valor en pesos en la interfaz.", headerIcon: Scale },
];

export default function AdminWalletConfigTab() {
    const [wallet, setWallet] = useState(null);
    const [settings, setSettings] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    const load = async () => {
        const [w, s] = await Promise.all([
            api.get("/admin/wallet-config"),
            api.get("/admin/settings"),
        ]);
        setWallet(w.data);
        setSettings(s.data);
        setForm({ ...w.data, ...s.data });
    };

    useEffect(() => { load(); }, []);

    const baseValues = useMemo(() => ({ ...(wallet || {}), ...(settings || {}) }), [wallet, settings]);

    const dirty = useMemo(() => {
        if (!wallet || !settings) return false;
        return Object.keys(form).some((k) => String(form[k] ?? "") !== String(baseValues[k] ?? ""));
    }, [form, baseValues, wallet, settings]);

    const feesSum = useMemo(() => {
        const sum = ["platform_pct", "creator_pct", "league_pct"]
            .map((k) => Number(form[k] || 0))
            .reduce((a, b) => a + b, 0);
        return sum;
    }, [form]);
    const playersPct = Math.max(0, 1 - feesSum);
    const feesInvalid = feesSum >= 1.0;

    if (!wallet || !settings) {
        return <div className="h-64 bg-p2w-surface border border-p2w-line rounded-2xl animate-pulse" data-testid="wallet-config-loading" />;
    }

    const save = async () => {
        if (feesInvalid) {
            toast.error("Las comisiones suman ≥ 100%. Reduce algún porcentaje.");
            return;
        }
        const walletUpdates = {};
        const settingsUpdates = {};
        for (const f of ALL_FIELDS) {
            const v = form[f.k];
            if (v === undefined || v === null || v === "") continue;
            if (String(v) === String(baseValues[f.k] ?? "")) continue;
            const dest = f.scope === "wallet" ? walletUpdates : settingsUpdates;
            if (f.type === "number" || f.type === "percent") dest[f.k] = Number(v);
            else dest[f.k] = v;
        }
        if (Object.keys(walletUpdates).length === 0 && Object.keys(settingsUpdates).length === 0) {
            toast.message("No hay cambios para guardar");
            return;
        }
        setSaving(true);
        try {
            const tasks = [];
            if (Object.keys(walletUpdates).length) tasks.push(api.patch("/admin/wallet-config", walletUpdates));
            if (Object.keys(settingsUpdates).length) tasks.push(api.patch("/admin/settings", settingsUpdates));
            await Promise.all(tasks);
            await load();
            toast.success("Configuración guardada");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="wallet-config-tab">
            {GROUPS.map((g) => {
                const fields = ALL_FIELDS.filter((f) => f.group === g.id);
                if (fields.length === 0) return null;
                const theme = GROUP_THEMES[g.id];
                const HeaderIcon = g.headerIcon;
                return (
                    <div
                        key={g.id}
                        data-testid={`group-${g.id}`}
                        className={`relative overflow-hidden rounded-2xl border ${theme.border} bg-gradient-to-br from-p2w-surface to-p2w-elevated p-6`}
                    >
                        <div className={`glow-orb ${theme.glow} w-[280px] h-[280px] -top-32 -right-16 opacity-[0.12]`} />
                        <div className="relative">
                            <div className="flex items-start gap-4">
                                <div className={`w-11 h-11 rounded-xl border ${theme.badgeBorder} ${theme.badgeBg} flex items-center justify-center shrink-0`}>
                                    <HeaderIcon className={`w-5 h-5 ${theme.icon}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-sora font-bold text-lg text-white">{g.title}</h3>
                                    <p className="mt-1 text-sm text-p2w-gray">{g.desc}</p>
                                </div>
                            </div>

                            {g.id === "pozo" && (
                                <div className={`mt-5 rounded-xl border p-4 flex items-center justify-between gap-3 ${
                                    feesInvalid
                                        ? "border-p2w-red/40 bg-p2w-red/10"
                                        : "border-p2w-purple/30 bg-p2w-purple/[0.06]"
                                }`}>
                                    <div className="flex items-center gap-2">
                                        {feesInvalid && <AlertTriangle className="w-4 h-4 text-p2w-red" />}
                                        <div>
                                            <div className="text-[10px] uppercase tracking-widest text-p2w-gray">Reparto resultante</div>
                                            <div className="mt-0.5 text-sm">
                                                <span className="font-sora font-bold text-p2w-green number-tabular">Jugadores {(playersPct * 100).toFixed(1)}%</span>
                                                <span className="text-p2w-gray"> · Plataforma {((form.platform_pct || 0) * 100).toFixed(1)}%</span>
                                                <span className="text-p2w-gray"> · Creador {((form.creator_pct || 0) * 100).toFixed(1)}%</span>
                                                <span className="text-p2w-gray"> · Liga {((form.league_pct || 0) * 100).toFixed(1)}%</span>
                                            </div>
                                        </div>
                                    </div>
                                    {feesInvalid && (
                                        <span className="text-xs font-bold text-p2w-red uppercase tracking-wider">Suma {(feesSum * 100).toFixed(1)}% inválida</span>
                                    )}
                                </div>
                            )}

                            <div className="mt-5 grid sm:grid-cols-2 gap-4">
                                {fields.map((f) => (
                                    <FieldRow
                                        key={f.k}
                                        field={f}
                                        theme={theme}
                                        value={form[f.k] ?? ""}
                                        onChange={(v) => setForm((s) => ({ ...s, [f.k]: v }))}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                );
            })}

            <div className="sticky bottom-4 z-10 flex items-center justify-between gap-3 rounded-2xl border border-p2w-line bg-p2w-elevated/95 backdrop-blur p-4">
                <div className="text-sm text-p2w-gray">
                    {feesInvalid ? <span className="text-p2w-red">Las comisiones del pozo suman ≥ 100%.</span>
                        : dirty ? <span className="text-p2w-amber">Tienes cambios sin guardar.</span>
                        : "Todo guardado."}
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setForm({ ...baseValues })} disabled={!dirty || saving} className="border-p2w-line bg-transparent text-white hover:bg-p2w-surface hover:text-white" data-testid="wallet-config-reset">
                        Descartar
                    </Button>
                    <Button onClick={save} disabled={!dirty || saving || feesInvalid} data-testid="wallet-config-save" className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold gap-2 disabled:opacity-50">
                        <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar cambios"}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function FieldRow({ field, value, onChange, theme }) {
    const Icon = field.icon;
    const t = theme || {};
    if (field.type === "select") {
        return (
            <div data-testid={`field-${field.k}`} className={`rounded-xl border border-p2w-line bg-p2w-elevated/60 px-3 py-3 transition ${t.fieldHover || ""}`}>
                <Label className="text-xs uppercase tracking-widest text-p2w-gray flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-md border ${t.badgeBorder || "border-p2w-line"} ${t.badgeBg || "bg-p2w-surface"} flex items-center justify-center`}>
                        <Icon className={`w-3 h-3 ${t.icon || "text-p2w-gray"}`} />
                    </span>
                    {field.label}
                </Label>
                <Select value={String(value)} onValueChange={onChange}>
                    <SelectTrigger
                        data-testid={`input-${field.k}`}
                        className={`mt-2 bg-p2w-surface border-p2w-line text-white ${t.focus || ""}`}
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-p2w-elevated border-p2w-line text-white">
                        {field.options.map(([v, l]) => (
                            <SelectItem key={v} value={v}>{l}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {field.help && <p className="mt-1.5 text-xs text-p2w-gray">{field.help}</p>}
            </div>
        );
    }
    return (
        <div data-testid={`field-${field.k}`} className={`rounded-xl border border-p2w-line bg-p2w-elevated/60 px-3 py-3 transition ${t.fieldHover || ""}`}>
            <Label className="text-xs uppercase tracking-widest text-p2w-gray flex items-center gap-2">
                <span className={`w-6 h-6 rounded-md border ${t.badgeBorder || "border-p2w-line"} ${t.badgeBg || "bg-p2w-surface"} flex items-center justify-center`}>
                    <Icon className={`w-3 h-3 ${t.icon || "text-p2w-gray"}`} />
                </span>
                {field.label}
            </Label>
            <Input
                data-testid={`input-${field.k}`}
                type={field.type === "percent" ? "number" : field.type}
                step={field.step}
                value={value === null || value === undefined ? "" : value}
                onChange={(e) => onChange(field.type === "text" ? e.target.value : Number(e.target.value))}
                className={`mt-2 bg-p2w-surface border-p2w-line text-white font-mono ${t.focus || ""}`}
            />
            {field.help && <p className="mt-1.5 text-xs text-p2w-gray">{field.help}</p>}
        </div>
    );
}
